import { useEffect, useState, useCallback, useRef } from 'react';
import { alpha, styled } from '@mui/material/styles';
import { Avatar, Badge, Box, IconButton, Typography, Chip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@emotion/react';
import { useNavigate } from 'react-router-dom';
import { useWSContext } from '../../context/WebSocketContext';

// Badge estilizado
const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#44b700',
    color: '#44b700',
    boxShadow: `0 0 0 2px ${theme.palette.dark.divider}`,
    '&::after': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      animation: 'ripple 1.2s infinite ease-in-out',
      border: '1px solid currentColor',
      content: '""',
    },
  },
  '@keyframes ripple': {
    '0%': {
      transform: 'scale(.8)',
      opacity: 1,
    },
    '100%': {
      transform: 'scale(2.4)',
      opacity: 0,
    },
  },
}));

export default function BoxClienteAtendimento({
  cliente,
  onClick,
  active,
  alertMessage,
  isReconnecting,
}) {
  const theme = useTheme();
  const { removeChat, chatsEncerrados } = useWSContext();
  const navigate = useNavigate();

  const [openAlert, setOpenAlert] = useState(true);
  const [statusInfo, setStatusInfo] = useState({
    label: 'Em atendimento',
    color: 'success',
    variant: 'outlined',
  });

  const prevIsReconnecting = useRef(isReconnecting);
  const isClosed = chatsEncerrados.includes(cliente.id);
  const isDisabled = isClosed;

  useEffect(() => {
    let removalTimer;
    if (isClosed) {
      removalTimer = setTimeout(() => {
        removeChat(cliente.id);
      }, 4000);
    }
    return () => {
      if (removalTimer) {
        clearTimeout(removalTimer);
      }
    };
  }, [isClosed, removeChat, cliente.id]);

  useEffect(() => {
    let timer;
    if (isClosed) {
      setStatusInfo({
        label: 'Chat encerrado',
        color: 'error',
        variant: 'filled',
      });
    } else if (isReconnecting) {
      setStatusInfo({
        label: 'Reconectando...',
        color: 'warning',
        variant: 'filled',
      });
    } else if (prevIsReconnecting.current && !isReconnecting) {
      setStatusInfo({
        label: 'Conexão restaurada!',
        color: 'success',
        variant: 'filled',
      });
      timer = setTimeout(() => {
        setStatusInfo({
          label: 'Em atendimento',
          color: 'success',
          variant: 'outlined',
        });
      }, 3000);
    } else {
      setStatusInfo({
        label: 'Em atendimento',
        color: 'success',
        variant: 'outlined',
      });
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [isReconnecting, isClosed]);

  useEffect(() => {
    prevIsReconnecting.current = isReconnecting;
  }, [isReconnecting]);

  const selectBox = useCallback(() => {
    if (isDisabled) return;
    onClick();
    setOpenAlert(true);
  }, [isDisabled, onClick]);

  function handleOpenAlert() {
    if (alertMessage === true) {
      setOpenAlert(false);
    }
  }

  useEffect(handleOpenAlert, [alertMessage]);

  // Ao clicar no X, redireciona para o chat do cliente e indica para abrir o modal de encerramento
  const handleRequestClose = (e) => {
    e.stopPropagation();
    if (isDisabled) return;
    navigate('/app/chat', {
      state: {
        ...cliente,
        wantClose: true, // Sinaliza para abrir o modal de encerramento automaticamente no chat
      },
    });
  };

  const renderStatusChip = () => (
    <Chip
      sx={{ width: '100%' }}
      label={statusInfo.label}
      color={statusInfo.color}
      variant={statusInfo.variant}
      size="small"
    />
  );

  return (
    <Box
      onClick={selectBox}
      tabIndex={0}
      sx={{
        display: 'flex',
        mb: 0.5,
        borderRadius: 1,
        background: theme.palette.gradients.dark,
        cursor: isDisabled && !isReconnecting ? 'not-allowed' : 'pointer',
        opacity: isDisabled && !isReconnecting ? 0.6 : 1,
        transition: 'opacity 0.3s ease-in-out, border 0.2s ease-in-out',
        border: `1px solid ${theme.palette.divider}`,
        '&:focus-visible': {
          outline: 'none',
          border: `1px solid ${theme.palette.primary.main}`,
          borderRadius: 2,
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          width: '100%',
          p: 0.5,
          textDecoration: 'none',
          borderRadius: 1,
          background: active
            ? alpha(theme.palette.primary.main, 0.5)
            : 'transparent',
        }}
      >
        <IconButton
          aria-label="fechar chat"
          color="default"
          sx={{
            position: 'absolute',
            top: 4,
            left: 4,
            zIndex: 1,
          }}
          onClick={handleRequestClose}
          disabled={isDisabled}
        >
          <CloseIcon fontSize="small" sx={{ width: 16, height: 16 }} />
        </IconButton>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            m: 1,
          }}
        >
          <StyledBadge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            variant="dot"
            invisible={isDisabled}
          >
            <Avatar
              alt={cliente?.name}
              src={cliente?.avatar}
              sx={{ width: 30, height: 30 }}
            />
          </StyledBadge>
          <Typography variant="subtitle2" sx={{ color: 'text.primary', ml: 1 }}>
            {cliente?.name}
          </Typography>
        </Box>
        <Badge
          badgeContent="nova"
          color="error"
          invisible={openAlert}
          sx={{ width: '90%' }}
        >
          {renderStatusChip()}
        </Badge>
      </Box>
    </Box>
  );
}
